import { ListKeyManager } from "@angular/cdk/a11y";
import { DOWN_ARROW, ENTER, SHIFT, TAB, UP_ARROW } from "@angular/cdk/keycodes";
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ContentChild,
	ElementRef,
	EventEmitter,
	forwardRef,
	HostBinding,
	HostListener,
	inject,
	Input,
	NgZone,
	OnInit,
	Output,
	QueryList,
	TemplateRef,
	ViewChild,
	ViewChildren,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormBuilder } from "@angular/forms";

import { KeyboardService } from "../../services/keyboard.service";

const INPUT_CONTROL_ACCESSOR = {
	provide: NG_VALUE_ACCESSOR,
	useExisting: forwardRef(() => NumberSuggestionCellInputComponent),
	multi: true,
};

@Component({
	selector: "ic-number-suggestion-input-cell",
	styleUrls: ["./number-suggestion-cell.component.scss"],
	providers: [INPUT_CONTROL_ACCESSOR],
	templateUrl: "./number-suggestion-cell.component.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class NumberSuggestionCellInputComponent implements ControlValueAccessor, OnInit {
	private readonly cdRef = inject(ChangeDetectorRef);
	private readonly eRef = inject(ElementRef);
	private readonly ngZone = inject(NgZone);
	private readonly keyBoardService = inject(KeyboardService);
	private readonly formBuilder = inject(UntypedFormBuilder);

	keyboardEventsManager: ListKeyManager<any>;
	preventBlurOnKeyEvent = false;
	itemIndex = 0;
	showList = false;
	disabled = false;
	form = this.formBuilder.group({
		value: [""],
	});

	private blurredFromGoingUp = false;
	private focussed = false;
	private listener = this.onClick.bind(this);

	@Input() min: number;
	@Input() max: number;
	@Input() key?: string;
	@Input() errorText = "";
	@Input() suffix = "";
	@Input() items: any[] = [];

	@Output() blurEvent = new EventEmitter();
	@Output() focusEvent = new EventEmitter();

	@ContentChild("suffixTmpl", { static: false }) suffixTemplate: TemplateRef<any>;
	@ContentChild("itemTmpl", { static: false }) itemTemplate: TemplateRef<any>;

	@ViewChildren("listItem") listItems: QueryList<any>;

	@ViewChild("list", { static: false }) list: ElementRef;
	@ViewChild("input", { static: true }) input: ElementRef;

	@HostBinding("class.error") @Input() error = false;
	@HostBinding("class.is-hidden") hideLabel = true;
	@HostBinding("attr.tabIndex") tabIndex = 0;

	@HostListener("focus")
	onFocussed() {
		if (this.showList) {
			this.eRef.nativeElement.blur();
		}

		if (this.blurredFromGoingUp) {
			return;
		}

		this.focus();
	}

	@HostListener("blur")
	onBlurred() {
		this.blurredFromGoingUp = false;
	}

	@HostListener("click")
	onClicked() {
		if (!this.blurredFromGoingUp) {
			return;
		}

		this.blurredFromGoingUp = false;
		this.toggleList();
	}

	@HostListener("keydown", ["$event"])
	onKeydown(event: KeyboardEvent) {
		if (this.showList || this.preventBlurOnKeyEvent || event.keyCode !== TAB || this.keyBoardService.isDown(SHIFT)) {
			return;
		}

		this.toggleList();

		return false;
	}

	private onTouch = () => {}; // eslint-disable-line @typescript-eslint/no-empty-function
	private onModelChange = (_: unknown) => {};

	get label() {
		const value = this.form.get("value").value;

		return value === 0 || value ? value : ".";
	}

	private navigate(event: KeyboardEvent) {
		const isDown = event.keyCode === DOWN_ARROW;
		const newIndex = isDown ? this.itemIndex + 1 : this.itemIndex - 1;
		const items = this.listItems.toArray();

		if (newIndex < 0 || newIndex > items.length - 1) {
			return false;
		}

		const current = items[newIndex];
		const previous = isDown ? items[newIndex - 1] : items[newIndex + 1];

		if (current) {
			current.nativeElement.focus();
			this.list.nativeElement.scrollTo(0, current.nativeElement.clientHeight * this.itemIndex);
		}

		if (previous) {
			previous.nativeElement.blur();
		}
	}

	private listenToClick() {
		this.ngZone.runOutsideAngular(() => {
			window.addEventListener("click", this.listener);
		});
	}

	private removeClick() {
		window.removeEventListener("click", this.listener);
	}

	private onClick(event: MouseEvent) {
		event.preventDefault();

		if (this.eRef.nativeElement.contains(event.target) || this.focussed) {
			// Prevents auto-selecting the first item if focus was set by clicking on another item
			this.focussed = false;

			return;
		}

		this.ngZone.run(() => {
			this.showList = false;
			this.onTouch();
			this.cdRef.markForCheck();
		});

		this.removeClick();
	}

	writeValue(value: number) {
		this.form.patchValue({ value });
		this.cdRef.markForCheck();
	}

	registerOnChange(fn: (v: unknown) => void) {
		this.onModelChange = fn;
	}

	registerOnTouched(fn: () => void) {
		this.onTouch = fn;
	}

	setDisabledState(isDisabled: boolean) {
		if (isDisabled) {
			this.form.disable();
		} else {
			this.form.enable();
		}

		this.disabled = isDisabled;
		this.cdRef.markForCheck();
	}

	showInput() {
		this.hideLabel = false;
		this.focusEvent.emit();
	}

	onChange(value: number | string) {
		// Check if the value is an empty string, if not convert it to a number
		const convertedValue = value !== "" ? Number(value) : "";

		this.setLabel(convertedValue as number);
		this.onModelChange(convertedValue);
	}

	setLabel(value: number) {
		this.form.patchValue({ value });
	}

	blurOnInput(event: FocusEvent) {
		if (this.preventBlurOnKeyEvent) {
			event.preventDefault();
		}

		if (!this.keyBoardService.isDown(SHIFT)) {
			return;
		}

		this.blur(0);
	}

	tabOnInput(event: KeyboardEvent, value: string) {
		if (value === "") {
			return;
		}

		event.preventDefault();
		this.preventBlurOnKeyEvent = true;
		this.blurInput();
	}

	// List navigation
	toggleList() {
		if (this.disabled) {
			return;
		}

		this.showList = !this.showList;

		if (this.showList) {
			this.preventBlurOnKeyEvent = false;
			this.hideLabel = false;
			this.focusEvent.emit();
			this.listenToClick();
		} else {
			this.removeClick();
			this.itemIndex = 0;
		}

		this.cdRef.markForCheck();
	}

	selectItem(item: any) {
		const value = this.key ? item[this.key] : item;
		this.setLabel(value);
		this.onTouch();
		this.onChange(value);
		this.blurInput();
	}

	// Set focus on input
	focus() {
		this.focussed = true;
		this.input.nativeElement.focus();
	}

	blur(index: number) {
		const shiftDown = this.keyBoardService.isDown(SHIFT);
		const tabbing = this.keyBoardService.isDown(TAB);
		if (tabbing && ((index === 0 && shiftDown) || (index === this.items.length - 1 && !shiftDown))) {
			this.blurredFromGoingUp = index === 0 && shiftDown;
			this.blurInput();
		}

		return;
	}

	blurInput() {
		this.hideLabel = true;
		this.itemIndex = 0;

		this.onTouch();
		this.toggleList();
		this.cdRef.markForCheck();

		this.input.nativeElement.blur();
		this.blurEvent.emit();
	}

	handleKeyUp(event: KeyboardEvent, item?: any) {
		// Prevents auto-selecting the first item if focus was set by pressing enter on another item
		if (this.focussed) {
			this.focussed = false;

			return;
		}

		if (!this.showList) {
			return;
		}

		if (event.keyCode === DOWN_ARROW || event.keyCode === UP_ARROW) {
			this.navigate(event);
		}

		if (event.keyCode === ENTER) {
			this.selectItem(item);
		}
	}

	setItemIndex(index: number) {
		this.itemIndex = index;
	}

	ngOnInit() {
		this.keyboardEventsManager = new ListKeyManager(this.listItems);
	}
}
